/* Editorial Booking — dark 3-step flow modeled exactly on the user's reference.
   Layout (top → bottom):
     · Eyebrow "RESERVE YOUR SESSION" + big serif "Book a Session"
     · 3-step stepper: 1 PACKAGE | 2 DATE & TIME | 3 DETAILS & PAYMENT
     · 3-column grid:
         LEFT  — Service cards stacked vertically (image + eyebrow + name + meta)
         MID   — Selected service detail: eyebrow + name + body + tier list
         RIGHT — Sticky "YOUR SESSION" summary card (service/tier/date/time/total/deposit)
     · Footer action bar — "← BACK" left, "CONTINUE →" right
   On step 2, the middle column swaps to date + time pickers; on step 3 to a
   customer details form. The summary sidebar stays present in all 3 steps. */

import { useEffect, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useBookingFlow, formatPrice } from "@/hooks/useBookingFlow";
import BookingTermsAcceptance from "@/components/BookingTermsAcceptance";
import { tenantHref } from "@/lib/tenant";

const STEPS = [
  { n: 1, label: "PACKAGE" },
  { n: 2, label: "DATE & TIME" },
  { n: 3, label: "DETAILS & PAYMENT" },
];

/** Renders "Mar 15" from a YYYY-MM-DD string. Uses the browser's locale
 *  so international clients see native formatting. Falls back to the
 *  raw string if the date isn't parseable. */
function formatDateLabel(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function EditorialBooking() {
  const { settings } = useSiteSettings();
  const f = useBookingFlow({ totalSteps: 3 });
  const navigate = useNavigate();

  // Map the shared 4-step hook to this 3-act narrative.
  // Act 1 = package+tier (hook step 1)
  // Act 2 = date+time   (hook step 2)
  // Act 3 = details     (hook step 3 + 4 collapsed)
  const stage = f.step <= 1 ? 1 : f.step === 2 ? 2 : 3;

  // Smooth-scroll the middle column into view whenever the stage
  // changes. On mobile the layout stacks vertically (services on top,
  // middle column, summary card, then the Continue button at the very
  // bottom of the page). After tapping Continue the page advances but
  // the user is still scrolled near the action bar — without this they
  // have to scroll UP to find the new date picker / details form, which
  // reads as a broken transition. Anchoring on the MIDDLE column (not
  // the page top) preserves their reading place inside the form rather
  // than yanking them back to the masthead. We skip the very first
  // mount (when stage === 1 and the user just landed) so the page
  // doesn't pre-scroll past the hero stepper on initial load.
  const middleRef = useRef(null);
  const firstStageMount = useRef(true);
  useEffect(() => {
    if (firstStageMount.current) {
      firstStageMount.current = false;
      return;
    }
    const el = middleRef.current;
    if (!el) return;
    // Tiny rAF defer so the new column has painted before we scroll
    // (otherwise iOS Safari sometimes scrolls to the OLD element's
    // height, which then collapses underneath the viewport).
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [stage]);

  function next() {
    if (stage === 1) f.setStep(2);
    else if (stage === 2) f.setStep(3);
    else if (stage === 3) f.submit();
  }
  function back() {
    if (stage === 1) {
      navigate(tenantHref("/"));
      return;
    }
    f.setStep(stage === 2 ? 1 : 2);
  }

  const depositPct = Number(settings?.deposit_percentage ?? 0);
  const total = Number(f.selectedTier?.price ?? 0);
  const deposit = total > 0 && depositPct > 0 ? (total * depositPct) / 100 : 0;

  // Shared render for the "STUDIO / Studio Photoshoot / description / tiers"
  // block. Used inline right below the selected package card on mobile (so
  // the tier picker sits where the user's thumb is instead of stranded at
  // the bottom of the whole packages list), AND as the middle-column
  // content on desktop.
  const renderPackageDetail = ({ withContinue = false } = {}) => {
    if (!f.selected) return null;
    return (
      <>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#D17C1F)]">
          {(f.selected.eyebrow || f.selected.category || "Studio").toString().toUpperCase()}
        </div>
        <div className="font-serif text-3xl sm:text-4xl mt-2 break-words leading-[1.05]" style={{ fontFamily: "var(--tenant-serif, serif)" }}>{f.selected.name}</div>
        {f.selected.description && (
          <p className="mt-3 text-sm leading-relaxed text-[color:var(--tenant-fg,#F5F2EC)]/70">{f.selected.description}</p>
        )}
        <div className="mt-6 h-px bg-white/12" />
        <ul className="mt-2 divide-y divide-[color:var(--tenant-fg-line,rgba(245,242,236,0.10))]">
          {f.selected.tiers?.map((t) => {
            const active = t.name === f.tierName;
            return (
              <li key={t.name}>
                <button
                  type="button"
                  onClick={() => f.setTierName(t.name)}
                  data-testid={`editorial-booking-tier-${t.name}`}
                  className={`w-full text-left px-4 py-5 flex items-start justify-between gap-5 transition-colors ${active
                    ? "bg-[color:var(--tenant-accent,#D17C1F)]/15 text-[color:var(--tenant-fg,#F5F2EC)]"
                    : "text-[color:var(--tenant-fg,#F5F2EC)]/70 hover:text-[color:var(--tenant-fg,#F5F2EC)]"}`}
                >
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="font-serif text-xl inline-flex items-center gap-2" style={{ fontFamily: "var(--tenant-serif, serif)" }}>
                        {t.name}
                        {active && <span aria-hidden className="text-[color:var(--tenant-accent,#D17C1F)] text-base">✓</span>}
                      </div>
                      <div className="font-serif text-lg shrink-0" style={{ fontFamily: "var(--tenant-serif, serif)" }}>
                        {Number(t.price) === 0 ? "$0" : formatPrice(Number(t.price))}
                      </div>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 mt-1.5">
                      <div className="text-xs text-[color:var(--tenant-fg,#F5F2EC)]/55 leading-relaxed">
                        {t.description || "Please call"}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)]/45 shrink-0">
                        {t.duration_minutes ? `${t.duration_minutes} MIN` : ""}
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Mobile: once a tier is picked, drop a prominent "Continue with
            [TIER]" CTA right below the list so the client's next action
            sits within the same viewport as their choice. Skipped on
            desktop (the sticky "CONTINUE →" bar in the RIGHT summary
            card handles that path). */}
        {withContinue && f.tierName && f.selected.tiers?.some((t) => t.name === f.tierName) && (
          <button
            type="button"
            onClick={next}
            data-testid="editorial-booking-continue-inline"
            className="anim-fade-slide-in mt-6 w-full h-14 px-5 flex items-center justify-between gap-3 bg-[color:var(--tenant-fg,#F5F2EC)] text-[color:var(--tenant-bg,#0A0908)] text-[11px] uppercase tracking-[0.32em] font-medium hover:opacity-90 transition-opacity"
          >
            <span>Continue with {f.tierName}</span>
            <span aria-hidden>→</span>
          </button>
        )}
      </>
    );
  };

  return (
    <div
      className="min-h-screen bg-[color:var(--tenant-bg,#0A0908)] text-[color:var(--tenant-fg,#F5F2EC)]"
      data-testid="editorial-booking"
      style={{ fontFamily: "var(--tenant-sans, system-ui)" }}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-24 pb-32">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#D17C1F)] mb-5">
          Reserve your session
        </div>
        <h1
          className="leading-[0.95] tracking-tight"
          style={{ fontFamily: "var(--tenant-serif, serif)", fontSize: "clamp(2.75rem, 6vw, 5rem)" }}
        >
          Book a Session
        </h1>

        {/* ── Stepper ─────────────────────────────────────────────────── */}
        <div className="mt-16 flex items-center gap-3 sm:gap-8 flex-wrap" data-testid="editorial-booking-stepper">
          {STEPS.map((s, i) => {
            const active = stage === s.n;
            const done = stage > s.n;
            return (
              <div key={s.n} className="flex items-center gap-3 sm:gap-5">
                <div className={`w-9 h-9 flex items-center justify-center text-xs font-medium ${active ? "bg-[color:var(--tenant-fg,#F5F2EC)] text-[color:var(--tenant-bg,#0A0908)]" : done ? "bg-[color:var(--tenant-accent,#D17C1F)] text-[color:var(--tenant-bg,#0A0908)]" : "border border-[color:var(--tenant-fg-line,rgba(245,242,236,0.30))] text-[color:var(--tenant-fg,#F5F2EC)]/55"}`}>
                  {s.n}
                </div>
                <div className={`text-[10px] uppercase tracking-[0.32em] ${active ? "text-[color:var(--tenant-fg,#F5F2EC)]" : done ? "text-[color:var(--tenant-fg,#F5F2EC)]/75" : "text-[color:var(--tenant-fg,#F5F2EC)]/45"}`}>
                  {s.label}
                </div>
                {i < STEPS.length - 1 && <div className="hidden sm:block w-12 h-px bg-white/15" />}
              </div>
            );
          })}
        </div>

        {/* ── 3-column body ─────────────────────────────────────────── */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-[1.05fr_1.25fr_0.95fr] gap-6 lg:gap-8">
          {/* LEFT — service list (always visible) */}
          <div className="min-w-0 space-y-4" data-testid="editorial-booking-services">
            <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent,#D17C1F)] mb-3">
              Choose your session & tier
            </div>
            {f.services.map((s, i) => {
              const sel = (s.id === f.selected?.id) || (s.slug === f.selected?.slug);
              const startFrom = Math.min(...(s.tiers?.map(t => Number(t.price)).filter(n => Number.isFinite(n)) || [0]));
              const fromLabel = Number.isFinite(startFrom) && startFrom >= 0 ? `$${startFrom}` : "—";
              const tierCount = s.tiers?.length || 0;
              const eyebrow = (s.eyebrow || s.category || "Studio").toString().toUpperCase();
              return (
                <Fragment key={s.id || i}>
                <button
                  type="button"
                  onClick={() => f.setSelectedId(s.id || s.slug)}
                  data-testid={`editorial-booking-card-${i}`}
                  className={`group w-full text-left bg-[color:var(--tenant-muted,#0E0B07)] border ${sel ? "border-[#F5F2EC]" : "border-[color:var(--tenant-fg-line,rgba(245,242,236,0.15))] hover:border-white/35"} transition-colors p-4 flex items-center gap-4 relative`}
                >
                  <div className="w-20 h-20 bg-[color:var(--tenant-fg-line,rgba(245,242,236,0.05))] overflow-hidden shrink-0">
                    {s.image_url ? (
                      <img src={s.image_url} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[color:var(--tenant-fg,#F5F2EC)]/25 text-[9px] uppercase tracking-[0.32em]">{eyebrow}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#D17C1F)]">{eyebrow}</div>
                    <div className="font-serif text-xl leading-tight mt-1 truncate" style={{ fontFamily: "var(--tenant-serif, serif)" }}>{s.name}</div>
                    <div className="text-xs text-[color:var(--tenant-fg,#F5F2EC)]/55 mt-1.5">from {fromLabel} · {tierCount} tier{tierCount === 1 ? "" : "s"}</div>
                  </div>
                  {sel && (
                    <div className="absolute top-3 right-3 text-[color:var(--tenant-fg,#F5F2EC)]" aria-hidden>✓</div>
                  )}
                </button>
                {/* Mobile-only inline detail: when this card is the selected
                    package, render the description + tier list DIRECTLY
                    below it, so the user's next tap target (their tier) is
                    right where their thumb already is instead of stranded
                    at the bottom of the whole packages list. Hidden at ≥lg
                    where the desktop middle column takes over. */}
                {sel && stage === 1 && (
                  <div
                    key={`inline-${s.id || i}`}
                    className="lg:hidden anim-fade-slide-in bg-[color:var(--tenant-muted,#0E0B07)] border border-t-0 border-[color:var(--tenant-fg-line,rgba(245,242,236,0.15))] p-5"
                    data-testid={`editorial-booking-detail-inline-${i}`}
                  >
                    {renderPackageDetail({ withContinue: true })}
                  </div>
                )}
                </Fragment>
              );
            })}
            {!f.loading && f.services.length === 0 && (
              <div
                data-testid="editorial-booking-empty"
                className="border border-[color:var(--tenant-fg-line,rgba(245,242,236,0.15))] p-6 text-[color:var(--tenant-fg,#F5F2EC)]/70 text-sm leading-relaxed"
              >
                <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#D17C1F)] mb-3">
                  Sessions opening soon
                </div>
                This studio hasn&rsquo;t published its sessions yet. Please check back shortly, or reach out via the contact page to inquire directly.
              </div>
            )}
          </div>

          {/* MIDDLE — desktop-only context panel. The mobile client now
              sees the tier picker inline below their selected package
              card (see the map above), so this column is hidden on
              mobile for stage 1 to prevent a duplicate render. It
              re-appears at stage 2 (date/time) and stage 3 (details)
              where there's no per-card inline surrogate. `middleRef` is
              still targeted by the stage-change scroll effect. */}
          <div ref={middleRef} className={`${stage === 1 ? "hidden lg:block" : ""} min-w-0 bg-[color:var(--tenant-muted,#0E0B07)] border border-[color:var(--tenant-fg-line,rgba(245,242,236,0.12))] p-6 lg:p-8 min-h-[28rem] scroll-mt-24`} data-testid="editorial-booking-detail">
            {stage === 1 && renderPackageDetail()}

            {stage === 2 && (
              <div data-testid="editorial-booking-schedule">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#D17C1F)]">When works</div>
                <div className="font-serif text-3xl mt-3 mb-7" style={{ fontFamily: "var(--tenant-serif, serif)" }}>Pick a date & time.</div>
                <label className="block mb-6">
                  <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)]/60">Date</span>
                  <input
                    type="date" value={f.date} onChange={(e) => f.setDate(e.target.value)}
                    data-testid="editorial-booking-date"
                    className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg-line,rgba(245,242,236,0.30))] focus:border-[#F5F2EC] py-3 mt-2 outline-none text-xl"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)]/60">Window</span>
                  <select value={f.time} onChange={(e) => f.setTime(e.target.value)}
                    data-testid="editorial-booking-time"
                    className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg-line,rgba(245,242,236,0.30))] focus:border-[#F5F2EC] py-3 mt-2 outline-none text-xl">
                    {["09:00","10:00","11:00","13:00","14:00","15:00","16:00"].map((t) => (
                      <option key={t} value={t} className="bg-[color:var(--tenant-muted,#0E0B07)]">{t}</option>
                    ))}
                  </select>
                </label>

                {/* Mobile-only inline CTA — mirrors the stage-1 "Continue with
                    [TIER]" pattern. Renders only once BOTH the date and a
                    time window are picked so it never asks the client to
                    advance with a half-filled slot. Hidden on desktop where
                    the footer action-bar handles it. */}
                {f.date && f.time && (
                  <button
                    key={`continue-datetime-${f.date}-${f.time}`}
                    type="button"
                    onClick={next}
                    data-testid="editorial-booking-continue-datetime"
                    className="lg:hidden anim-fade-slide-in mt-8 w-full h-14 px-5 flex items-center justify-between gap-3 bg-[color:var(--tenant-fg,#F5F2EC)] text-[color:var(--tenant-bg,#0A0908)] text-[11px] uppercase tracking-[0.32em] font-medium hover:opacity-90 transition-opacity"
                  >
                    <span>
                      Continue with {formatDateLabel(f.date)} @ {f.time}
                    </span>
                    <span aria-hidden>→</span>
                  </button>
                )}
              </div>
            )}

            {stage === 3 && (
              <div data-testid="editorial-booking-details">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#D17C1F)]">Final touches</div>
                <div className="font-serif text-3xl mt-3 mb-7" style={{ fontFamily: "var(--tenant-serif, serif)" }}>Your details.</div>
                {[
                  { k: "name",  l: "Full name",        t: "text",  ph: "Jane Doe" },
                  { k: "email", l: "Email",            t: "email", ph: "jane@studio.com" },
                  { k: "phone", l: "Phone (optional)", t: "tel",   ph: "+1 555 0100" },
                ].map((field) => (
                  <label key={field.k} className="block mb-5">
                    <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)]/60">{field.l}</span>
                    <input type={field.t} value={f.details[field.k]} placeholder={field.ph}
                      data-testid={`editorial-booking-${field.k}`}
                      onChange={(e) => f.setDetails({ ...f.details, [field.k]: e.target.value })}
                      className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg-line,rgba(245,242,236,0.30))] focus:border-[#F5F2EC] py-3 mt-2 outline-none text-lg placeholder:text-[color:var(--tenant-fg,#F5F2EC)]/30" />
                  </label>
                ))}
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)]/60">Anything we should know</span>
                  <textarea rows={3} value={f.details.notes} placeholder="A sentence on the moment you'd like remembered."
                    onChange={(e) => f.setDetails({ ...f.details, notes: e.target.value })}
                    data-testid="editorial-booking-notes"
                    className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg-line,rgba(245,242,236,0.30))] focus:border-[#F5F2EC] py-3 mt-2 outline-none text-base placeholder:text-[color:var(--tenant-fg,#F5F2EC)]/30 resize-none" />
                </label>
                <div className="mt-8">
                  <BookingTermsAcceptance
                    policyText={f.policyText}
                    checked={f.termsAccepted}
                    onChange={f.setTermsAccepted}
                    testidPrefix="editorial-booking-terms"
                  />
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — sticky summary sidebar */}
          <aside className="bg-[color:var(--tenant-muted,#0E0B07)] border border-[color:var(--tenant-fg-line,rgba(245,242,236,0.12))] p-6 lg:p-7 self-start lg:sticky lg:top-8" data-testid="editorial-booking-summary">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent,#D17C1F)] mb-6">
              Your session
            </div>
            <dl className="space-y-4">
              {[
                ["Service",  f.selected?.name],
                ["Tier",     f.selectedTier?.name],
                ["Date",     f.date],
                ["Time",     stage >= 2 ? f.time : null],
              ].map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-4 pb-2 border-b border-[color:var(--tenant-fg-line,rgba(245,242,236,0.10))]">
                  <dt className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)]/55">{k}</dt>
                  <dd className={`text-right font-serif text-base ${v ? "text-[color:var(--tenant-fg,#F5F2EC)]" : "text-[color:var(--tenant-fg,#F5F2EC)]/35"}`} style={{ fontFamily: "var(--tenant-serif, serif)" }}>
                    {v || "—"}
                  </dd>
                </div>
              ))}
              <div className="pt-4 space-y-4">
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)]/55">Total</dt>
                  <dd className={`text-right font-serif text-lg ${total > 0 ? "text-[color:var(--tenant-fg,#F5F2EC)]" : "text-[color:var(--tenant-fg,#F5F2EC)]/35"}`} style={{ fontFamily: "var(--tenant-serif, serif)" }}>
                    {total > 0 ? formatPrice(total) : "—"}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#F5F2EC)]/55">Deposit ({depositPct}%)</dt>
                  <dd className={`text-right font-serif text-base ${deposit > 0 ? "text-[color:var(--tenant-accent,#D17C1F)]" : "text-[color:var(--tenant-fg,#F5F2EC)]/35"}`} style={{ fontFamily: "var(--tenant-serif, serif)" }}>
                    {deposit > 0 ? formatPrice(deposit) : "—"}
                  </dd>
                </div>
              </div>
            </dl>
          </aside>
        </div>

        {/* ── Action bar ──────────────────────────────────────────────── */}
        <div className="mt-12 flex items-center justify-between gap-4" data-testid="editorial-booking-actions">
          <button
            type="button"
            onClick={back}
            data-testid="editorial-booking-back"
            className="text-[10px] uppercase tracking-[0.36em] text-[color:var(--tenant-fg,#F5F2EC)]/55 hover:text-[color:var(--tenant-fg,#F5F2EC)] transition-colors"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={next}
            disabled={!f.canForward || f.submitting}
            data-testid="editorial-booking-next"
            className={`inline-flex items-center gap-3 px-8 h-12 text-[10px] uppercase tracking-[0.38em] transition-opacity ${
              f.canForward && !f.submitting
                ? "bg-[color:var(--tenant-fg,#F5F2EC)] text-[color:var(--tenant-bg,#0A0908)] hover:bg-[color:var(--tenant-accent,#D17C1F)]"
                : "bg-[color:var(--tenant-fg,#F5F2EC)]/45 text-[color:var(--tenant-bg,#0A0908)]/60 cursor-not-allowed"
            }`}
          >
            {f.submitting ? "Sending…" : stage < 3 ? "Continue →" : "Reserve session →"}
          </button>
        </div>
      </div>
    </div>
  );
}
